/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import { useContext, useRef, useEffect, useMemo, useState } from 'react';
import { isHexadecimal, length } from 'class-validator';
import { AppContext } from './contexts/AppContext';
import { useNimbusContext } from './contexts/NimbusContext';
import { NimbusResult } from '../lib/nimbus';
import { SettingsContext } from './contexts/SettingsContext';
import { useLocalization } from '@fluent/react';
import { FtlMsgResolver } from 'fxa-react/lib/utils';
import { getDefault } from '../lib/config';
import {
  DefaultIntegrationFlags,
  IntegrationFactory,
} from '../lib/integrations';
import { ReachRouterWindow } from '../lib/window';
import { StorageData, UrlHashData, UrlQueryData } from '../lib/model-data';
import {
  MetricsData,
  SignedInAccountStatus,
} from '../components/App/interfaces';
import {
  RelierClientInfo,
  RelierSubscriptionInfo,
  RelierCmsInfo,
  RelierLegalTerms,
  DefaultCmsContent,
} from './integrations';
import * as Sentry from '@sentry/browser';
import { useDynamicLocalization } from '../contexts/DynamicLocalizationContext';
import { sessionToken } from '../lib/cache';
import { useLocalStorageSync } from '../lib/hooks/useLocalStorageSync';
import {
  getFullAccountData,
  isSignedIn as checkIsSignedIn,
} from '../lib/account-storage';

const DEFAULT_CMS_ENTRYPOINT = 'default';

// Define the hook's return type, mimicking useQuery's structure
interface FetchState<T> {
  loading: boolean;
  error?: Error;
  data?: T;
}

export function useAccount() {
  const { account } = useContext(AppContext);
  if (!account) {
    throw new Error('Are you forgetting an AppContext.Provider?');
  }
  return account;
}

function getMissing(obj: Record<string, unknown>) {
  const missingKeys: string[] = [];
  for (const x of Object.keys(obj)) {
    if (obj[x] == null) {
      missingKeys.push(x);
    }
  }

  return missingKeys.join(',');
}

export function useAuthClient() {
  const { authClient } = useContext(AppContext);
  if (!authClient) {
    throw new Error(
      `Are you forgetting an AppContext.Provider? State:${getMissing({
        authClient,
      })}`
    );
  }
  return authClient;
}

export function useSensitiveDataClient() {
  const { sensitiveDataClient } = useContext(AppContext);
  if (!sensitiveDataClient) {
    throw new Error(
      `Are you forgetting an AppContext.Provider? State:${getMissing({
        sensitiveDataClient,
      })}`
    );
  }
  return sensitiveDataClient;
}

export function useIntegration() {
  const clientInfoState = useClientInfoState();
  const productInfoState = useProductInfoState();
  const cmsInfoState = useCmsInfoState();
  const legalTermsState = useLegalTermsState();

  return useMemo(() => {
    // If we are still loading data, just return an null integration
    if (
      clientInfoState.loading ||
      productInfoState.loading ||
      cmsInfoState.loading ||
      legalTermsState.loading
    ) {
      return null;
    }

    const windowWrapper = new ReachRouterWindow();
    const urlQueryData = new UrlQueryData(windowWrapper);
    const urlHashData = new UrlHashData(windowWrapper);
    const storageData = new StorageData(windowWrapper);

    const flags = new DefaultIntegrationFlags(urlQueryData, storageData);

    // Extract legal terms from REST API result
    const legalTerms = legalTermsState.data?.legalTerms;

    const integrationFactory = new IntegrationFactory({
      flags,
      window: windowWrapper,
      clientInfo: clientInfoState.data?.clientInfo,
      // useClientInfoState is the source of truth for whether the
      // /v1/oauth/client/:id fetch was attempted and failed; the factory
      // can't infer this from URL state alone because subsequent init
      // steps may populate `data.clientId` from non-query sources.
      clientInfoLoadFailed: !!clientInfoState.error,
      cmsInfo: cmsInfoState.data?.cmsInfo,
      legalTerms,
      productInfo: productInfoState.data?.productInfo,
      data: urlQueryData,
      channelData: urlHashData,
      storageData,
    });

    return integrationFactory.getIntegration();
  }, [clientInfoState, productInfoState, cmsInfoState, legalTermsState]);
}

/**
 * A hook to provide the Nimbus experiments within components.
 * This hook uses the NimbusContext to get experiment data.
 *
 * @returns the NimbusResult with experiment information, or null if not available.
 */
export function useExperiments(): NimbusResult | null {
  const { experiments } = useNimbusContext();
  return experiments;
}

export function useSession() {
  const { session } = useContext(AppContext);
  if (!session) {
    throw new Error('Are you forgetting an AppContext.Provider?');
  }
  return session;
}

export function useConfig() {
  const { config } = useContext(AppContext);
  if (!config) {
    return getDefault();
  }
  return config;
}

export function useInitialMetricsQueryState() {
  const { authClient } = useContext(AppContext);
  const [state, setState] = useState<{
    loading: boolean;
    error?: Error;
    data?: { account: MetricsData };
  }>({ loading: true });

  useEffect(() => {
    let mounted = true;

    const fetchMetricsData = async () => {
      const token = sessionToken();
      if (!token) {
        if (mounted) {
          setState({ loading: false, data: undefined });
        }
        return;
      }

      try {
        const cachedData = getFullAccountData();
        if (cachedData && cachedData.uid) {
          if (mounted) {
            setState({
              loading: false,
              data: {
                account: {
                  uid: cachedData.uid,
                  recoveryKey: cachedData.recoveryKey,
                  metricsEnabled: cachedData.metricsEnabled,
                  primaryEmail: cachedData.primaryEmail,
                  emails: cachedData.emails,
                  totp: cachedData.totp,
                },
              },
            });
          }
          return;
        }

        if (!authClient) {
          throw new Error('AuthClient not available');
        }

        const [accountResult, totpResult, recoveryKeyResult] =
          await Promise.allSettled([
            authClient.account(token),
            authClient.checkTotpTokenExists(token),
            authClient.recoveryKeyExists(token, undefined),
          ]);

        const accountData =
          accountResult.status === 'fulfilled' ? accountResult.value : null;
        const totpData =
          totpResult.status === 'fulfilled' ? totpResult.value : null;
        const recoveryKeyData =
          recoveryKeyResult.status === 'fulfilled'
            ? recoveryKeyResult.value
            : null;

        if (mounted && accountData) {
          const emails = accountData.emails || [];
          setState({
            loading: false,
            data: {
              account: {
                uid: accountData.uid,
                recoveryKey: recoveryKeyData
                  ? {
                      exists: recoveryKeyData.exists,
                      estimatedSyncDeviceCount:
                        recoveryKeyData.estimatedSyncDeviceCount,
                    }
                  : null,
                metricsEnabled: accountData.metricsEnabled ?? true,
                primaryEmail:
                  emails.find((e: { isPrimary?: boolean }) => e.isPrimary) ||
                  null,
                emails,
                totp: totpData || null,
              },
            },
          });
        } else if (mounted) {
          setState({ loading: false, data: undefined });
        }
      } catch (error) {
        if (mounted) {
          setState({
            loading: false,
            error: error instanceof Error ? error : new Error('Unknown error'),
          });
        }
      }
    };

    fetchMetricsData();

    return () => {
      mounted = false;
    };
  }, [authClient]);

  return state;
}

export function useClientInfoState() {
  const { config } = useContext(AppContext);
  const [state, setState] = useState<{
    loading: boolean;
    error?: Error;
    data?: { clientInfo: RelierClientInfo };
  }>({ loading: true });

  const urlQueryData = new UrlQueryData(new ReachRouterWindow());
  const clientId =
    urlQueryData.get('client_id') || urlQueryData.get('service') || '';

  const isValidClientId = isHexadecimal(clientId) && length(clientId, 16);

  useEffect(() => {
    let mounted = true;
    if (!config) {
      Sentry.addBreadcrumb({
        message: `OAuth Client - Missing config`,
        category: 'useClientInfoState.fetch',
      });
      setState((prev) => ({ ...prev, loading: false, error: new Error('Missing config') }));
      return;
    }
    if (!isValidClientId) {
      Sentry.addBreadcrumb({
        message: `OAuth Client - Invalid clientId`,
        category: 'useClientInfoState.fetch',
      });
      setState((prev) => ({ ...prev, loading: false, error: new Error('Invalid clientId') }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    // Tracks the controller for the request currently in flight so the cleanup
    // can abort it on unmount. A fresh controller is created per attempt (below)
    // so a per-attempt timeout doesn't permanently abort later retries.
    let activeController: AbortController | undefined;

    (async () => {
      // It's important this resolves! If it doesn't, down stream calls will be in an erroneous state.
      // Retry with linear back off before giving up; both bounds are configurable.
      const MAX_RETRIES = config.oauth.clientInfoRetries;
      const REQUEST_TIMEOUT_MS = config.oauth.clientInfoTimeout;
      for (let i=0; i < MAX_RETRIES; i++) {
        // Fresh controller per attempt: a timeout aborts only this attempt, and
        // the cleanup can abort whichever request is currently in flight.
        const controller = new AbortController();
        activeController = controller;
        const timeoutId = setTimeout(
          () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
          REQUEST_TIMEOUT_MS
        );
        try {
          const response = await fetch(
            `${config.servers.auth.url}/v1/oauth/client/${clientId}`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              signal: controller.signal,
            }
          );
          if (!mounted) {
            return Sentry.addBreadcrumb({
              message: `OAuth Client - Unmounted after fetch`,
              category: 'useClientInfoState.fetch',
              data: { attempt:i }
            });
          }
          Sentry.addBreadcrumb({
            message: `OAuth Client - Got response`,
            category: 'useClientInfoState.fetch',
            data: { clientId, ok:response?.ok, status:response?.status }
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch client info: ${response.status}`);
          }

          const data = await response.json();
          if (!mounted) {
            return Sentry.addBreadcrumb({
              message: `OAuth Client - Unmounted after response.json()`,
              category: 'useClientInfoState.fetch',
              data: { attempt:i }
            });
          }
          Sentry.addBreadcrumb({
            message: `OAuth Client - Got response data`,
            category: 'useClientInfoState.fetch',
            data: {
              attempt:i,
              ...data
            }
          });

          // Success! Set state and return to exit loop...
          return setState({
            loading: false,
            data: {
              clientInfo: {
                clientId: data.id || clientId,
                imageUri: data.image_uri || null,
                redirectUri: data.redirect_uri || null,
                serviceName: data.name || null,
                trusted: data.trusted || false,
              },
            },
          });
        } catch (error) {
          // An abort triggered by unmount (not a timeout) is an intentional
          // cancellation — don't capture it to Sentry or retry.
          if (!mounted) {
            return Sentry.addBreadcrumb({
              message: `OAuth Client - Unmounted after expected error`,
              category: 'useClientInfoState.fetch',
              data: { attempt:i }
            });
          }

          const err =
            error instanceof Error ? error : new Error('Unknown error');

          Sentry.addBreadcrumb({
            message: 'OAuth Client - Unexpected Error',
            category: 'useClientInfoState.fetch',
            data: {
              mounted,
              attempt: i,
              clientId,
              message: err.message,
              errno: error.errno,
              code: error.code,
              statusCode: error.statusCode,
            }
          })


          // On last attempt, give up and record failure to sentry
          if (i === MAX_RETRIES - 1) {
            setState({ loading: false, error: err });

            // Surface fetch failures as their own Sentry issue so the real cause
            // (network, WAF challenge, 5xx, unknown client_id) is observable
            // separately from the downstream scope-validation paths it used to
            // be misattributed to — see FXA-13618.
            Sentry.captureException(err, {
              tags: {
                area: 'useClientInfoState.fetch',
                clientId
              },
              extra: {
                mounted,
                attempt: i,
                message: err.message,
                errno: error.errno,
                code: error.code,
                statusCode: error.statusCode,
              },
            });
          } else {
            // Back off and try again
            await new Promise(r => setTimeout(r, i * 250));
            if (!mounted) {
              return Sentry.addBreadcrumb({
                message: `OAuth Client - Unmounted after backoff`,
                category: 'useClientInfoState.fetch',
                data: { attempt:i }
              });
            }
          }
        } finally {
          // Always clear the per-attempt timeout so it can't fire late or leak.
          clearTimeout(timeoutId);
        }
      }
    })();

    return () => {
      mounted = false;
      // Cancel any request currently in flight.
      activeController?.abort();
    };
  }, [clientId, isValidClientId, config]);

  return state;
}

export function useCmsInfoState() {
  const { config } = useContext(AppContext);
  if (!config) {
    throw new Error('Are you forgetting an AppContext.Provider?');
  }

  const authUrl = config?.servers.auth.url;
  const { currentLocale } = useDynamicLocalization();

  const urlQueryData = new UrlQueryData(new ReachRouterWindow());
  const clientId = urlQueryData.get('client_id');

  // If entrypoint is not provided in the URL, set a "default" value.
  // CMS customizations that have a default entrypoint created will get loaded, otw
  // the fallback experience will be loaded.
  const entrypoint = urlQueryData.get('entrypoint') || DEFAULT_CMS_ENTRYPOINT;

  const [state, setState] = useState<
    FetchState<{ cmsInfo: RelierCmsInfo | undefined }>
  >({
    loading: false,
    error: undefined,
    data: undefined,
  });

  useEffect(() => {
    // We disable the CMS if:
    // 1. CMS is not enabled in the config
    // 2. l10nEnabled is false AND user's locale is not English
    // 3. User language toggle is not English
    // 4. The clientId is not provided or is not a valid 16 digit hex
    //
    // These steps are a best effort to prevent users from seeing a page with both English and non-English text.

    function shouldFetchCms() {
      // If l10nEnabled is true, fetch cms config for any locale
      if (config?.cms?.l10nEnabled === true) {
        return true;
      }

      // If l10nEnabled is false, only fetch for English locales
      // Check both browser language and user's selected locale
      let isEnglish = false;
      if (currentLocale) {
        isEnglish = currentLocale.startsWith('en');
      } else if (navigator.language) {
        isEnglish = navigator.language.startsWith('en');
      }

      return isEnglish;
    }

    if (
      !config.cms.enabled ||
      !shouldFetchCms() ||
      !clientId ||
      !isHexadecimal(clientId) ||
      !length(clientId, 16) ||
      !entrypoint
    ) {
      setState({ loading: false, error: undefined, data: undefined });
      return;
    }

    let mounted = true;
    setState((prev) => ({ ...prev, loading: true }));

    const fetchConfig = async () => {
      const fetchStart = performance.now();
      try {
        const url = new URL(`${authUrl}/v1/cms/config`);
        url.searchParams.append('clientId', clientId);
        url.searchParams.append('entrypoint', entrypoint);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': currentLocale,
          },
        });

        let config: RelierCmsInfo | undefined;

        if (!response.ok) {
          Sentry.captureMessage('Non-OK response from CMS fetchConfig.', {
            tags: { area: 'useCmsInfoState' },
            extra: { clientId, entrypoint, status: response.status },
          });
        }

        config = await response.json();

        if (mounted) {
          setState({
            loading: false,
            error: undefined,
            data: { cmsInfo: config },
          });
        }
      } catch (error) {
        const fetchDuration = Math.round(performance.now() - fetchStart);
        const cancelled = error instanceof Error && error.name === 'AbortError';
        Sentry.captureException(error, {
          tags: { area: 'useCmsInfoState' },
          extra: {
            clientId,
            entrypoint,
            fetchDuration,
            cancelled,
            errorName: error instanceof Error ? error.name : typeof error,
          },
        });

        if (mounted) {
          setState({
            loading: false,
            error: error instanceof Error ? error : new Error('Unknown error'),
            data: undefined,
          });
        }
      }
    };

    fetchConfig();

    return () => {
      mounted = false;
    };
  }, [
    authUrl,
    clientId,
    entrypoint,
    config.cms.enabled,
    config.cms?.l10nEnabled,
    currentLocale,
  ]);

  return state;
}

export function useDefaultCmsState({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { config } = useContext(AppContext);
  if (!config) {
    throw new Error('Are you forgetting an AppContext.Provider?');
  }

  const authUrl = config?.servers.auth.url;
  const { currentLocale } = useDynamicLocalization();

  const [state, setState] = useState<
    FetchState<{ defaultCms: DefaultCmsContent | undefined }>
  >({
    loading: false,
    error: undefined,
    data: undefined,
  });

  useEffect(() => {
    if (!enabled || !config.cms.enabled) {
      setState({ loading: false, error: undefined, data: undefined });
      return;
    }

    let mounted = true;
    setState((prev) => ({ ...prev, loading: true }));

    const fetchDefaultCms = async () => {
      try {
        const response = await fetch(`${authUrl}/v1/cms/default`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': currentLocale,
          },
        });

        if (!response.ok) {
          Sentry.captureMessage('Non-OK response from CMS fetchDefault.', {
            tags: { area: 'useDefaultCmsState' },
            extra: { status: response.status },
          });
          if (mounted) {
            setState({
              loading: false,
              error: new Error(
                `CMS default fetch failed (status ${response.status})`
              ),
              data: undefined,
            });
          }
          return;
        }

        const defaultCms: DefaultCmsContent | undefined = await response.json();

        if (mounted) {
          setState({
            loading: false,
            error: undefined,
            data: { defaultCms },
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { area: 'useDefaultCmsState' },
        });
        if (mounted) {
          setState({
            loading: false,
            error: error instanceof Error ? error : new Error('Unknown error'),
            data: undefined,
          });
        }
      }
    };

    fetchDefaultCms();

    return () => {
      mounted = false;
    };
  }, [authUrl, enabled, config.cms.enabled, currentLocale]);

  return state;
}

const emptyProductInfoState: {
  loading: boolean;
  error?: Error;
  data?: { productInfo: RelierSubscriptionInfo };
} = { loading: false };

export function useProductInfoState() {
  return emptyProductInfoState;
}

export function useLegalTermsState() {
  const { config } = useContext(AppContext);
  if (!config) {
    throw new Error('Are you forgetting an AppContext.Provider?');
  }

  const authUrl = config?.servers.auth.url;
  const { currentLocale } = useDynamicLocalization();

  const urlQueryData = new UrlQueryData(new ReachRouterWindow());
  const clientId = urlQueryData.get('client_id');
  const service = urlQueryData.get('service');
  const context = urlQueryData.get('context');

  const [state, setState] = useState<
    FetchState<{ legalTerms: RelierLegalTerms | undefined }>
  >({
    loading: false,
    error: undefined,
    data: undefined,
  });

  useEffect(() => {
    // Determine if we should fetch by clientId or service
    const isOAuth = !!clientId;
    const isOAuthNative = context === 'oauth_webchannel_v1';

    let shouldFetch = false;
    let queryParam = '';
    let queryValue = '';

    if (
      isOAuth &&
      !isOAuthNative &&
      clientId &&
      isHexadecimal(clientId) &&
      length(clientId, 16)
    ) {
      // OAuth RP redirect flow - query by clientId
      shouldFetch = true;
      queryParam = 'clientId';
      queryValue = clientId;
    } else if (isOAuthNative && service) {
      // OAuth native flow - query by service
      shouldFetch = true;
      queryParam = 'service';
      queryValue = service;
    }

    if (!shouldFetch) {
      setState({ loading: false, error: undefined, data: undefined });
      return;
    }

    let mounted = true;
    setState((prev) => ({ ...prev, loading: true }));

    const fetchLegalTerms = async () => {
      const fetchStart = performance.now();
      try {
        const url = new URL(`${authUrl}/v1/cms/legal-terms`);
        url.searchParams.append(queryParam, queryValue);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': currentLocale,
          },
        });

        let legalTerms: RelierLegalTerms | null = null;

        if (!response.ok) {
          Sentry.captureMessage('Non-OK response from legal terms endpoint.', {
            tags: { area: 'useLegalTermsState' },
            extra: { queryParam, queryValue, status: response.status },
          });
        }

        // Check if response has content before parsing
        const responseText = await response.text();

        if (responseText) {
          try {
            legalTerms = JSON.parse(responseText);
          } catch (parseError) {
            Sentry.captureException(parseError, {
              tags: { area: 'useLegalTermsState' },
              extra: { responseText },
            });
          }
        }

        if (mounted) {
          setState({
            loading: false,
            error: undefined,
            data: legalTerms ? { legalTerms } : undefined,
          });
        }
      } catch (error) {
        const fetchDuration = Math.round(performance.now() - fetchStart);
        const cancelled = error instanceof Error && error.name === 'AbortError';
        Sentry.captureException(error, {
          tags: { area: 'useLegalTermsState' },
          extra: {
            queryParam,
            queryValue,
            fetchDuration,
            cancelled,
            errorName: error instanceof Error ? error.name : typeof error,
          },
        });

        if (mounted) {
          setState({
            loading: false,
            error: error instanceof Error ? error : new Error('Unknown error'),
            data: undefined,
          });
        }
      }
    };

    fetchLegalTerms();

    return () => {
      mounted = false;
    };
  }, [authUrl, clientId, service, context, currentLocale]);

  return state;
}

// TODO: FXA-8286, test pattern for container components, which will determine
// how we want to handle `useQuery` (e.g., directly) and tests.

export function useLocalSignedInQueryState() {
  const accountsData = useLocalStorageSync('accounts');
  const currentAccountUid = useLocalStorageSync('currentAccountUid');
  useLocalStorageSync('isSignedIn');

  const isSignedIn = useMemo(() => {
    void accountsData;
    void currentAccountUid;
    return checkIsSignedIn();
  }, [accountsData, currentAccountUid]);

  return {
    loading: false,
    data: { isSignedIn } as SignedInAccountStatus,
  };
}

export function useAlertBar() {
  const { alertBarInfo } = useContext(SettingsContext);
  if (!alertBarInfo) {
    throw new Error('Are you forgetting a SettingsContext.Provider?');
  }
  return alertBarInfo;
}

export function useFtlMsgResolver() {
  const config = useConfig();
  const { l10n } = useLocalization();
  return new FtlMsgResolver(l10n, config.l10n.strict);
}

export function useNotifier() {
  return {
    onAccountSignIn(_account: unknown) {
      // FOLLOW-UP: Not yet implemented.
    },
  };
}

/**
 * Hook to run a function on an interval.
 * @param callback - function to call
 * @param delay - interval in ms to run, null to stop poll
 */
export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!delay && delay !== 0) {
      return;
    }

    const id = window.setInterval(() => {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }, delay);

    return () => window.clearInterval(id);
  }, [delay]);
}
